// Master Menu Module and Screen API Service
import { apiGet, apiPost, apiPut, apiDelete } from '../services/apiClient';

// Helper to wrap response in { data: ... } format for backward compatibility
const wrapResponse = (data) => ({ data });

/**
 * Master Menu Module APIs
 */

/**
 * Get all active menu modules
 * GET /gateway/registration/api/signup/master-menu-modules/all
 */
export const getModules = async () => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/master-menu-modules/all');
    
    let modules = [];
    if (response?.data?.data && Array.isArray(response.data.data)) {
      modules = response.data.data;
    } else if (Array.isArray(response.data)) {
      modules = response.data;
    } else if (Array.isArray(response)) {
      modules = response;
    }
    
    return wrapResponse(modules);
  } catch (error) {
    console.error('Error fetching modules:', error);
    throw error;
  }
};

/**
 * Get all menu modules (both active and inactive)
 * GET /gateway/registration/api/signup/master-menu-modules/all-including-inactive
 */
export const getModulesIncludingInactive = async () => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/master-menu-modules/all-including-inactive');
    
    let modules = [];
    if (response?.data?.data && Array.isArray(response.data.data)) {
      modules = response.data.data;
    } else if (Array.isArray(response.data)) {
      modules = response.data;
    } else if (Array.isArray(response)) {
      modules = response;
    }
    
    return wrapResponse(modules);
  } catch (error) {
    console.error('Error fetching all modules:', error);
    throw error;
  }
};

/**
 * Get all active menu modules with pagination
 * GET /gateway/registration/api/signup/master-menu-modules/paginated?page=0&size=10
 */
export const getModulesPaginated = async (page = 0, size = 10) => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/master-menu-modules/paginated', {
      page,
      size
    });
    
    return wrapResponse(response?.data || response);
  } catch (error) {
    console.error('Error fetching paginated modules:', error);
    throw error;
  }
};

/**
 * Create a new menu module
 * POST /gateway/registration/api/signup/master-menu-modules/create
 */
export const createModule = async (moduleData) => {
  try {
    const response = await apiPost('/gateway/registration/api/signup/master-menu-modules/create', moduleData);
    return wrapResponse(response?.data || response);
  } catch (error) {
    console.error('Error creating module:', error);
    throw error;
  }
};

/**
 * Update a menu module
 * PUT /gateway/registration/api/signup/master-menu-modules/{moduleId}
 */
export const updateModule = async (moduleId, moduleData) => {
  try {
    const response = await apiPut(`/gateway/registration/api/signup/master-menu-modules/${moduleId}`, moduleData);
    return wrapResponse(response?.data || response);
  } catch (error) {
    console.error('Error updating module:', error);
    throw error;
  }
};

/**
 * Delete a menu module
 * DELETE /gateway/registration/api/signup/master-menu-modules/{moduleId}
 */
export const deleteModule = async (moduleId) => {
  try {
    const response = await apiDelete(`/gateway/registration/api/signup/master-menu-modules/${moduleId}`);
    return wrapResponse(response?.data || response);
  } catch (error) {
    console.error('Error deleting module:', error);
    throw error;
  }
};

/**
 * Master Screens APIs
 */

/**
 * Get all active screens grouped by module
 * GET /gateway/registration/api/signup/master-screens/getall
 */
export const getScreensGroupedByModule = async () => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/master-screens/getall');
    
    let screens = {};
    if (response?.data?.data && typeof response.data.data === 'object') {
      screens = response.data.data;
    } else if (typeof response.data === 'object') {
      screens = response.data;
    } else if (typeof response === 'object') {
      screens = response;
    }
    
    return wrapResponse(screens);
  } catch (error) {
    console.error('Error fetching screens grouped by module:', error);
    throw error;
  }
};

/**
 * Get all screens (both active and inactive) grouped by module
 * GET /gateway/registration/api/signup/master-screens/getall-including-inactive
 */
export const getScreensGroupedByModuleIncludingInactive = async () => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/master-screens/getall-including-inactive');
    
    let screens = {};
    if (response?.data?.data && typeof response.data.data === 'object') {
      screens = response.data.data;
    } else if (typeof response.data === 'object') {
      screens = response.data;
    } else if (typeof response === 'object') {
      screens = response;
    }
    
    return wrapResponse(screens);
  } catch (error) {
    console.error('Error fetching all screens grouped by module:', error);
    throw error;
  }
};

/**
 * Get all active screens with pagination
 * GET /gateway/registration/api/signup/master-screens/paginated?page=0&size=10
 */
export const getScreensPaginated = async (page = 0, size = 10) => {
  try {
    const response = await apiGet('/gateway/registration/api/signup/master-screens/paginated', {
      page,
      size
    });
    
    return wrapResponse(response?.data || response);
  } catch (error) {
    console.error('Error fetching paginated screens:', error);
    throw error;
  }
};

/**
 * Create a new screen
 * POST /gateway/registration/api/signup/master-screens
 */
export const createScreen = async (screenData) => {
  try {
    const response = await apiPost('/gateway/registration/api/signup/master-screens', screenData);
    return wrapResponse(response?.data || response);
  } catch (error) {
    console.error('Error creating screen:', error);
    throw error;
  }
};

/**
 * Update a screen
 * PUT /gateway/registration/api/signup/master-screens/{screenId}
 */
export const updateScreen = async (screenId, screenData) => {
  try {
    const response = await apiPut(`/gateway/registration/api/signup/master-screens/${screenId}`, screenData);
    return wrapResponse(response?.data || response);
  } catch (error) {
    console.error('Error updating screen:', error);
    throw error;
  }
};

/**
 * Delete a screen
 * DELETE /gateway/registration/api/signup/master-screens/{screenId}
 */
export const deleteScreen = async (screenId) => {
  try {
    const response = await apiDelete(`/gateway/registration/api/signup/master-screens/${screenId}`);
    return wrapResponse(response?.data || response);
  } catch (error) {
    console.error('Error deleting screen:', error);
    throw error;
  }
};

export const masterMenuModuleService = {
  getModules,
  getModulesIncludingInactive,
  getModulesPaginated,
  createModule,
  updateModule,
  deleteModule,
  getScreensGroupedByModule,
  getScreensGroupedByModuleIncludingInactive,
  getScreensPaginated,
  createScreen,
  updateScreen,
  deleteScreen
};

